import { useEffect, useRef, useState } from "react";
import { IconButton, CircularProgress, Tooltip } from "@mui/material";
import MicIcon from '@mui/icons-material/Mic';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import { waSendAudio } from "../../utils/wa/sendaudio";
import { openGlobalSnack } from "../utils/snack";

const MAX_SECONDS=300;
const TOUCH=48;

type Phase='idle'|'recording'|'review'|'sending';

function pickMimeType():string|undefined{
  const list=['audio/webm;codecs=opus','audio/ogg;codecs=opus','audio/mp4','audio/webm'];
  return list.find(t=>typeof MediaRecorder!=='undefined'&&MediaRecorder.isTypeSupported(t));
}
function fmt(s:number){
  return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
}

// Gravador de áudio do atendente: toque no microfone -> grava -> parar -> ouvir -> enviar/descartar.
// Mobile first: alvos de toque de 48px, barra ocupa toda a largura e não depende de hover.
export function AudioRecorder({chatId,disabled,disabledReason}:{chatId:number,disabled?:boolean,disabledReason?:string}){
  const [phase,setPhase]=useState<Phase>('idle');
  const [seconds,setSeconds]=useState(0);
  const [blob,setBlob]=useState<Blob|undefined>();
  const [url,setUrl]=useState<string|undefined>();
  const recorder=useRef<MediaRecorder|null>(null);
  const stream=useRef<MediaStream|null>(null);
  const chunks=useRef<Array<Blob>>([]);
  const keep=useRef(true);

  useEffect(()=>{
    if(phase!=='recording')return;
    const t=setInterval(()=>setSeconds(s=>s+1),1000);
    return ()=>clearInterval(t);
  },[phase]);
  useEffect(()=>{
    if(phase==='recording'&&seconds>=MAX_SECONDS)stop(true);
  },[seconds]);
  useEffect(()=>()=>{ // desmontou (trocou de chat): solta o microfone
    keep.current=false;
    try{recorder.current?.state==='recording'&&recorder.current.stop()}catch(e){}
    stream.current?.getTracks().forEach(t=>t.stop());
  },[]);
  useEffect(()=>()=>{if(url)URL.revokeObjectURL(url)},[url]);

  async function start(){
    if(typeof MediaRecorder==='undefined'||!navigator.mediaDevices?.getUserMedia){
      openGlobalSnack('Este navegador não suporta gravação de áudio.','error');
      return;
    }
    try{
      const s=await navigator.mediaDevices.getUserMedia({audio:true});
      stream.current=s;
      const mime=pickMimeType();
      const rec=new MediaRecorder(s,mime?{mimeType:mime}:undefined);
      chunks.current=[];
      keep.current=true;
      rec.ondataavailable=e=>{if(e.data.size>0)chunks.current.push(e.data)};
      rec.onstop=()=>{
        s.getTracks().forEach(t=>t.stop());
        if(!keep.current)return;
        const b=new Blob(chunks.current,{type:rec.mimeType||'audio/webm'});
        if(b.size===0){reset();return}
        setBlob(b);
        setUrl(URL.createObjectURL(b));
        setPhase('review');
      };
      recorder.current=rec;
      rec.start();
      setSeconds(0);
      setPhase('recording');
    }catch(e){
      openGlobalSnack('Permita o acesso ao microfone para gravar.','warning');
    }
  }
  function stop(keepAudio:boolean){
    keep.current=keepAudio;
    if(recorder.current?.state==='recording')recorder.current.stop();
    if(!keepAudio)reset();
  }
  function reset(){
    setBlob(undefined);
    setUrl(undefined);
    setSeconds(0);
    setPhase('idle');
  }
  async function send(){
    if(!blob)return;
    setPhase('sending');
    try{
      await waSendAudio(chatId,blob);
      reset();
    }catch(e:any){
      const status=e?.target?.status;
      openGlobalSnack(status===409?'Chat em atendimento por outro atendente.':status===429?'Muitos áudios em pouco tempo. Aguarde um instante.':'Erro ao enviar áudio.','error');
      setPhase('review');
    }
  }

  const btn={width:TOUCH,height:TOUCH} as const;
  if(phase==='idle'){
    return (
      <Tooltip title={disabled?(disabledReason||''):'Gravar áudio'}>
        <span>
          <IconButton aria-label="Gravar áudio" disabled={disabled} onClick={start} color="primary" sx={btn}><MicIcon/></IconButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <div style={{position:'absolute',left:0,right:0,bottom:0,zIndex:5,display:'flex',alignItems:'center',gap:4,padding:'6px 8px',background:'#fff',borderTop:'1px solid #e0e0e0',minHeight:60}}>
      <IconButton aria-label="Descartar áudio" disabled={phase==='sending'} onClick={()=>phase==='recording'?stop(false):reset()} sx={btn}><DeleteOutlineIcon color="error"/></IconButton>
      {phase==='recording'?<>
        <span style={{width:10,height:10,borderRadius:'50%',background:'#d32f2f',animation:'arpulse 1s infinite'}}/>
        <span style={{flex:1,fontVariantNumeric:'tabular-nums'}} aria-live="polite">Gravando {fmt(seconds)}</span>
        <IconButton aria-label="Parar gravação" onClick={()=>stop(true)} color="primary" sx={btn}><StopIcon/></IconButton>
      </>:<>
        <audio src={url} controls style={{flex:1,minWidth:0,height:40}}/>
        {phase==='sending'?<div style={{...btn,display:'flex',alignItems:'center',justifyContent:'center'}}><CircularProgress size={24}/></div>
          :<IconButton aria-label="Enviar áudio" onClick={send} color="primary" sx={btn}><SendIcon/></IconButton>}
      </>}
      <style>{`@keyframes arpulse{0%,100%{opacity:1}50%{opacity:.25}}`}</style>
    </div>
  );
}
